import type { ArrowObject as MapArrowObject } from '~/common/types';
import { BRIGHT_COLORS } from '~/constants/color';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { Point } from '../SurfaceBase';
import { getArrowPath, getArrowScale, STEM_LENGTH, ZOOM_THRESHOLD } from './common';

export default class ArrowObject extends CanvasObject {
  arrow: MapArrowObject;
  pointer: Path2D;
  lineAngle: number = 0;
  labelDimensions: {
    x: number;
    y: number;
    width: number;
    height: number;
  } = { x: 0, y: 0, width: 0, height: 0 };

  constructor(id: string, surface: SurfaceBase, arrow: MapArrowObject) {
    super(id, surface);
    this.arrow = arrow;
    this.pointer = getArrowPath();
  }

  computeLineAngle = (p1: Point, p2: Point): number => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    const a = -1 * Math.atan2(dx, dy) + Math.PI;
    return a;
  };

  computeAnchorPoint = (p1: Point, p2: Point) => {
    const zoom = this.surface.map.getZoom();
    let scale = 1;

    const a = { x: p1.x, y: p1.y };

    if (zoom <= ZOOM_THRESHOLD) {
      scale = getArrowScale(zoom);
    }

    if (p1.x <= p2.x) {
      a.x = a.x - STEM_LENGTH * scale;
    } else {
      a.x = a.x + STEM_LENGTH * scale;
    }

    return a;
  };

  renderLabel = (ctx: CanvasRenderingContext2D, p1: Point, p2: Point) => {
    const { properties, title } = this.arrow;
    const { subtype } = properties;

    if (subtype !== 'metadata' && !properties.showTitle) return;

    const padding = 10;
    const fontSize = 14;
    const labelOffset = 2 * padding;
    ctx.font = `bold ${fontSize}px arial`;
    const textWidth = ctx.measureText(title).width;

    const lineLength = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const exceedsLength = textWidth + padding > lineLength;

    if (exceedsLength && subtype !== 'metadata' && !properties.labelAlwaysVisible) {
      return;
    }

    const p = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };

    const rawRotation = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    let rotation: number = 0;

    if (rawRotation >= Math.PI / 2 && rawRotation <= Math.PI) {
      rotation = -1 * Math.PI + rawRotation;
    } else if (rawRotation > -1 * Math.PI && rawRotation < (-1 * Math.PI) / 2) {
      rotation = Math.PI + rawRotation;
    } else {
      rotation = rawRotation;
    }

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.translate(p.x, p.y);
    ctx.rotate(rotation);
    ctx.fillStyle = properties.color;
    ctx.beginPath();
    ctx.roundRect(-textWidth / 2 - padding / 2, -fontSize - labelOffset, textWidth + padding, fontSize + padding, [5]);
    ctx.fill();
    ctx.fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText(title, 0, -fontSize / 2 - labelOffset - padding / 4);
    ctx.restore();
  };

  renderLabelOnLeader = (ctx: CanvasRenderingContext2D, a: Point, p1: Point) => {
    const { properties, title, description } = this.arrow;
    const { subtype } = properties;
    const padding = 10;
    const fontSize = 14;
    const labelOffset = padding / 2;
    ctx.font = `bold ${fontSize}px arial`;
    if (subtype === 'metadata' && properties.font !== undefined) {
      ctx.font = `${fontSize}px ${properties.font}`;
    }
    const fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';

    let textLines: string[] = [];

    if (subtype === 'metadata' || properties.showTitle === true) {
      textLines.push(title);
    }

    if (subtype === 'metadata' || properties.showDescription) {
      const lines = description.trim() === '' ? ['Empty Text'] : description.split('\n');
      textLines = [...textLines, ...lines];
    }

    if (textLines.length === 0) {
      return;
    }

    const textHeight = fontSize * textLines.length;
    let textWidth: number = 0;

    for (let i = 0; i < textLines.length; i++) {
      const t = textLines[i];
      const w = ctx.measureText(t).width;
      textWidth = Math.max(textWidth, w);
    }

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.fillStyle = properties.color;
    ctx.translate(a.x, a.y);
    ctx.beginPath();
    if (a.x <= p1.x) {
      const x = -textWidth - labelOffset - padding;
      const y = -textHeight / 2 - padding / 2;
      const w = textWidth + padding;
      const h = textHeight + padding;
      this.labelDimensions = { x: a.x + x, y: a.y + y, width: w, height: h };
      ctx.textBaseline = 'top';
      ctx.textAlign = 'right';
      ctx.roundRect(x, y, w, h, [5]);
      ctx.fill();
      ctx.fillStyle = fillStyle;
      let yDiff = 0;
      for (let i = 0; i < textLines.length; i++) {
        ctx.fillText(textLines[i], -labelOffset - padding / 2, -textHeight / 2 + yDiff);
        yDiff += fontSize;
      }
    } else {
      const x = labelOffset;
      const y = -textHeight / 2 - padding / 2;
      const w = textWidth + padding;
      const h = textHeight + padding;
      this.labelDimensions = { x: a.x + x, y: a.y + y, width: w, height: h };
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.roundRect(x, y, w, h, [5]);
      ctx.fill();
      ctx.fillStyle = fillStyle;
      let yDiff = 0;
      for (let i = 0; i < textLines.length; i++) {
        ctx.fillText(textLines[i], labelOffset + padding / 2, -textHeight / 2 + yDiff);
        yDiff += fontSize;
      }
    }
    ctx.restore();
    return;
  };

  renderLeaderLabelHitArea = (ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, color: string) => {
    const { properties } = this.arrow;
    const { subtype } = properties;

    if (subtype !== 'metadata' && !properties.showTitle && !properties.showDescription) {
      return;
    }

    const { x, y, width, height } = this.labelDimensions;
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, [5]);
    ctx.fill();
    ctx.restore();
  };

  renderPointer = (ctx: CanvasRenderingContext2D, p1: Point, p2: Point) => {
    const { properties } = this.arrow;
    const zoom = this.surface.map.getZoom();

    ctx.save();
    ctx.lineWidth = properties.strokeWidth;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.translate(p2.x, p2.y);

    // if zoom is way out, shrink the arrow
    if (zoom <= ZOOM_THRESHOLD) {
      const s = getArrowScale(zoom);
      ctx.scale(s, s);
    }

    ctx.rotate(this.lineAngle);
    ctx.stroke(this.pointer);
    ctx.restore();
  };

  render(
    ctx: CanvasRenderingContext2D,
    strokeColor?: string | undefined,
    strokeWidth?: number | undefined,
    isDashed?: boolean | undefined,
    strokeOpacity?: number | undefined,
    isBorderHidden?: boolean | undefined,
  ): void {
    const { properties } = this.arrow;
    const { subtype } = properties;
    const z = this.surface.map.getZoom();

    // for metadata arrows, we want to hide the object at the layer min zoom level.
    if (subtype === 'metadata' && z < properties.minZoom) {
      return;
    }

    const [c1, c2] = this.arrow.geometry.coordinates;
    const p1 = this.surface.toScreenCoords({ lng: c1[0], lat: c1[1] });
    const p2 = this.surface.toScreenCoords({ lng: c2[0], lat: c2[1] });
    this.lineAngle = this.computeLineAngle(p1, p2);

    ctx.save();
    ctx.globalAlpha = properties.strokeOpacity;
    ctx.strokeStyle = properties.color;
    ctx.lineWidth = properties.strokeWidth;
    properties.strokeStyle === 'dashed' && ctx.setLineDash([10, 4]);

    if (subtype === 'metadata' || properties.showAsLeader === true) {
      const a = this.computeAnchorPoint(p1, p2);
      ctx.beginPath();
      ctx.moveTo(p2.x, p2.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.lineTo(a.x, a.y);
      ctx.stroke();

      this.renderLabelOnLeader(ctx, a, p1);
    } else {
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
      this.renderLabel(ctx, p1, p2);
    }

    this.renderPointer(ctx, p1, p2);
    ctx.restore();
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    const { properties } = this.arrow;
    const { subtype } = properties;
    const z = this.surface.map.getZoom();

    if (subtype === 'metadata' && z < properties.minZoom) {
      return;
    }

    const [c1, c2] = this.arrow.geometry.coordinates;
    const p1 = this.surface.toScreenCoords({ lng: c1[0], lat: c1[1] });
    const p2 = this.surface.toScreenCoords({ lng: c2[0], lat: c2[1] });
    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = properties.strokeWidth * 3;
    if (subtype === 'metadata' || properties.showAsLeader === true) {
      const a = this.computeAnchorPoint(p1, p2);
      ctx.beginPath();
      ctx.moveTo(p2.x, p2.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.lineTo(a.x, a.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
    ctx.restore();
    this.renderLeaderLabelHitArea(ctx, color);
  }
}
